"use client";

import React, {
  CSSProperties,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { pow, round } from "mathjs";

import styles from "./gradual-blur.module.css";

type Position = "top" | "bottom" | "left" | "right";
type Curve = "linear" | "bezier" | "ease-in" | "ease-out" | "ease-in-out";
type Animated = boolean | "scroll";
type Target = "parent" | "page";
type Preset =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "subtle"
  | "intense"
  | "smooth"
  | "sharp"
  | "header"
  | "footer"
  | "sidebar"
  | "page-header"
  | "page-footer";

interface GradualBlurProps {
  position?: Position;
  strength?: number;
  height?: string;
  width?: string;
  divCount?: number;
  exponential?: boolean;
  zIndex?: number;
  animated?: Animated;
  duration?: string;
  easing?: string;
  opacity?: number;
  curve?: Curve;
  responsive?: boolean;
  target?: Target;
  className?: string;
  style?: CSSProperties;
  hoverIntensity?: number;
  preset?: Preset;
  overLight?: boolean;
  onAnimationComplete?: () => void;
  mobileHeight?: string;
  tabletHeight?: string;
  desktopHeight?: string;
  mobileWidth?: string;
  tabletWidth?: string;
  desktopWidth?: string;
}

const DEFAULT_CONFIG = {
  position: "bottom",
  strength: 2,
  height: "6rem",
  divCount: 5,
  exponential: false,
  zIndex: 1000,
  animated: false,
  duration: "0.3s",
  easing: "ease-out",
  opacity: 1,
  curve: "linear",
  responsive: false,
  target: "parent",
  className: "",
  style: {},
} satisfies Required<
  Pick<
    GradualBlurProps,
    | "position"
    | "strength"
    | "height"
    | "divCount"
    | "exponential"
    | "zIndex"
    | "animated"
    | "duration"
    | "easing"
    | "opacity"
    | "curve"
    | "responsive"
    | "target"
    | "className"
    | "style"
  >
>;

const PRESETS: Record<Preset, Partial<GradualBlurProps>> = {
  top: { position: "top", height: "6rem" },
  bottom: { position: "bottom", height: "6rem" },
  left: { position: "left", height: "6rem" },
  right: { position: "right", height: "6rem" },
  subtle: { height: "4rem", strength: 1, opacity: 0.8, divCount: 3 },
  intense: { height: "10rem", strength: 4, divCount: 8, exponential: true },
  smooth: { height: "8rem", curve: "bezier", divCount: 10 },
  sharp: { height: "5rem", curve: "linear", divCount: 4 },
  header: { position: "top", height: "8rem", curve: "ease-out" },
  footer: { position: "bottom", height: "8rem", curve: "ease-out" },
  sidebar: { position: "left", height: "6rem", strength: 2.5 },
  "page-header": { position: "top", height: "10rem", target: "page", strength: 3 },
  "page-footer": { position: "bottom", height: "10rem", target: "page", strength: 3 },
};

const CURVE_FUNCTIONS: Record<Curve, (progress: number) => number> = {
  linear: (progress) => progress,
  bezier: (progress) => progress * progress * (3 - 2 * progress),
  "ease-in": (progress) => progress * progress,
  "ease-out": (progress) => 1 - Math.pow(1 - progress, 2),
  "ease-in-out": (progress) =>
    progress < 0.5
      ? 2 * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 2) / 2,
};

const mergeConfigs = (...configs: Array<Record<string, unknown>>) =>
  configs.reduce((acc, current) => ({ ...acc, ...current }), {});

const getGradientDirection = (position: Position) => {
  const directionMap: Record<Position, string> = {
    top: "to top",
    bottom: "to bottom",
    left: "to left",
    right: "to right",
  };

  return directionMap[position] ?? "to bottom";
};

const debounce = <T extends (...args: never[]) => void>(fn: T, wait: number) => {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), wait);
  };
};

const useResponsiveDimension = (
  responsive: boolean,
  config: Record<string, unknown>,
  key: "height" | "width"
) => {
  const [value, setValue] = useState(config[key] as string | undefined);

  useEffect(() => {
    if (!responsive) return;

    const capitalizedKey = `${key[0].toUpperCase()}${key.slice(1)}`;

    const calculate = () => {
      const width = window.innerWidth;
      let nextValue = config[key] as string | undefined;

      if (width <= 480 && config[`mobile${capitalizedKey}`]) {
        nextValue = config[`mobile${capitalizedKey}`] as string;
      } else if (width <= 768 && config[`tablet${capitalizedKey}`]) {
        nextValue = config[`tablet${capitalizedKey}`] as string;
      } else if (width <= 1024 && config[`desktop${capitalizedKey}`]) {
        nextValue = config[`desktop${capitalizedKey}`] as string;
      }

      setValue(nextValue);
    };

    const debounced = debounce(calculate, 100);
    calculate();

    window.addEventListener("resize", debounced);

    return () => window.removeEventListener("resize", debounced);
  }, [responsive, config, key]);

  return responsive ? value : (config[key] as string | undefined);
};

const useIntersectionObserver = (
  ref: React.RefObject<HTMLDivElement | null>,
  shouldObserve = false
) => {
  const [isVisible, setIsVisible] = useState(!shouldObserve);

  useEffect(() => {
    if (!shouldObserve || !ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [ref, shouldObserve]);

  return isVisible;
};

function GradualBlur({
  hoverIntensity,
  animated,
  onAnimationComplete,
  ...props
}: GradualBlurProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const config = useMemo(() => {
    const presetConfig = props.preset ? PRESETS[props.preset] ?? {} : {};
    return mergeConfigs(DEFAULT_CONFIG, presetConfig, props) as Required<
      Omit<GradualBlurProps, "hoverIntensity" | "onAnimationComplete" | "preset">
    > &
      Pick<GradualBlurProps, "hoverIntensity" | "onAnimationComplete">;
  }, [props]);

  const responsiveHeight = useResponsiveDimension(config.responsive, config, "height");
  const responsiveWidth = useResponsiveDimension(config.responsive, config, "width");
  const isVisible = useIntersectionObserver(containerRef, config.animated === "scroll");

  const blurDivs = useMemo(() => {
    const layers: React.ReactNode[] = [];
    const increment = 100 / config.divCount;
    const currentStrength =
      isHovered && hoverIntensity ? config.strength * hoverIntensity : config.strength;
    const curveFunction = CURVE_FUNCTIONS[config.curve] ?? CURVE_FUNCTIONS.linear;

    for (let i = 1; i <= config.divCount; i += 1) {
      let progress = i / config.divCount;
      progress = curveFunction(progress);

      const blurValue = config.exponential
        ? Number(pow(2, progress * 4)) * 0.0625 * currentStrength
        : 0.0625 * (progress * config.divCount + 1) * currentStrength;

      const p1 = Number(round((increment * i - increment) * 10)) / 10;
      const p2 = Number(round(increment * i * 10)) / 10;
      const p3 = Number(round((increment * i + increment) * 10)) / 10;
      const p4 = Number(round((increment * i + increment * 2) * 10)) / 10;

      let gradient = `transparent ${p1}%, black ${p2}%`;
      if (p3 <= 100) gradient += `, black ${p3}%`;
      if (p4 <= 100) gradient += `, transparent ${p4}%`;

      const direction = getGradientDirection(config.position);
      const layerStyle: CSSProperties = {
        position: "absolute",
        inset: "0",
        maskImage: `linear-gradient(${direction}, ${gradient})`,
        WebkitMaskImage: `linear-gradient(${direction}, ${gradient})`,
        backdropFilter: `blur(${Number(blurValue).toFixed(3)}rem)`,
        WebkitBackdropFilter: `blur(${Number(blurValue).toFixed(3)}rem)`,
        opacity: config.opacity,
        transition:
          config.animated && config.animated !== "scroll"
            ? `backdrop-filter ${config.duration} ${config.easing}`
            : undefined,
      };

      layers.push(<div key={i} style={layerStyle} />);
    }

    return layers;
  }, [config, hoverIntensity, isHovered]);

  const containerStyle = useMemo(() => {
    const isVertical = ["top", "bottom"].includes(config.position);
    const isPageTarget = config.target === "page";
    const baseStyle: CSSProperties = {
      position: isPageTarget ? "fixed" : "absolute",
      pointerEvents: hoverIntensity ? "auto" : "none",
      opacity: isVisible ? 1 : 0,
      transition: config.animated ? `opacity ${config.duration} ${config.easing}` : undefined,
      zIndex: isPageTarget ? config.zIndex + 100 : config.zIndex,
      ...config.style,
    };

    if (isVertical) {
      baseStyle.height = responsiveHeight;
      baseStyle.width = responsiveWidth || "100%";
      baseStyle[config.position] = 0;
      baseStyle.left = 0;
      baseStyle.right = 0;
    } else {
      baseStyle.width = responsiveWidth || responsiveHeight;
      baseStyle.height = "100%";
      baseStyle[config.position] = 0;
      baseStyle.top = 0;
      baseStyle.bottom = 0;
    }

    return baseStyle;
  }, [config, hoverIntensity, isVisible, responsiveHeight, responsiveWidth]);

  useEffect(() => {
    if (isVisible && animated === "scroll" && onAnimationComplete) {
      const timeout = setTimeout(
        () => onAnimationComplete(),
        Number.parseFloat(config.duration) * 1000
      );

      return () => clearTimeout(timeout);
    }
  }, [animated, config.duration, isVisible, onAnimationComplete]);

  const className = [styles.root, config.className].filter(Boolean).join(" ");

  return (
    <div
      ref={containerRef}
      className={className}
      style={containerStyle}
      onMouseEnter={hoverIntensity ? () => setIsHovered(true) : undefined}
      onMouseLeave={hoverIntensity ? () => setIsHovered(false) : undefined}
    >
      <div className={styles.inner}>{blurDivs}</div>
    </div>
  );
}

export default memo(GradualBlur);
